/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  WAITING: 'waiting',
  EXECUTION: 'execution',
  MONITORING: 'monitoring',
  STOP: 'stop',
  ERROR: 'error',
  CONNECT_FAILED: 'connectionFailed'
};

export default {
  name: 'requirement123',
  data() {
    return {
      phase: PHASE.WAITING,
      systemId: '',
      isLoading: false,
      coolTime: null,
      timerElapsed: 0,
      isElapsedTimerWorking: false,
      timerInfoElapsed: '',
      interval: 1000,
      executeType: '',
      modal_work_support_123: false
    };
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.CONNECT_FAILED:
          return 'danger';
        case PHASE.STOP:
          return 'success';
        case PHASE.WAITING:
        case PHASE.EXECUTION:
        case PHASE.MONITORING:
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    // ==================================================================================
    // 画面遷移等
    // ==================================================================================

    /**
     * 初期化処理
     */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.setExecuteType();
      this.modal_work_support_123 = true;
    },

    /**
     * ExecuteAPI実行時type設定
     */
    setExecuteType() {
      if (this.workSupportId === 12001) {
        this.executeType = 'ws123_execute';
      } else {
        this.executeType = 'execute';
      }
    },

    /**
     * 待機画面、実行画面のキャンセルボタン押下時の処理
     */
    async stopProcess() {
      if (this.workSupportId === 12001) {
        // WS.120から遷移してきた場合、DefaultSession遷移を実施
        await this.exit();
      } else {
        // 通常時、ウィンドウを閉じる
        await this.closeWizard();
      }
    },

    /**
     * 実行画面表示
     */
    executeProcess() {
      this.phase = PHASE.EXECUTION;
    },

    /**
     * 実行監視画面表示
     */
    async monitorProcessExecution() {
      this.timerInfoElapsed = this.$t(`${this.i18nWsKey}.REQ_WS_123_TXT_3_0001`).replace("$TIME$", 0);
      this.phase = PHASE.MONITORING;
      this.isLoading = true;
      await this.measureElapsedTime();
      this.isElapsedTimerWorking = true;
      this.isLoading = false;
      await this.execute();
    },

    /**
     * 経過時間を表示する処理
     */
    async measureElapsedTime() {
      this.coolTime = setInterval(() => {
        if (!this.isElapsedTimerWorking) {
          this.timerElapsed = 0;
          return;
        }
        this.timerElapsed += this.interval;
        this.timerInfoElapsed = this.$t(`${this.i18nWsKey}.REQ_WS_123_TXT_3_0001`).replace("$TIME$", Math.ceil(this.timerElapsed / 1000));
      }, this.interval);
    },

    /**
     * 停止画面OKボタン押下時の処理
     */
    turnPage() {
      // WS.120から遷移してきた場合
      if (this.workSupportId === 12001) {
        this.returningWSName = 'ws123';
        // WS.120に戻る
        this.$emit("isReturn", this.returningWSName);
      }
      this.closeWizard();
    },

    /**
     * 機能終了
     */
    async closeWizard() {
      if (this.workSupportId !== 12001 || this.returningWSName !== 'ws123') {
        this.$emit('close-wizard');
      }
      this.modal_work_support_123 = false;
    },

    // ==================================================================================
    // 通信を伴う処理
    // ==================================================================================

    /**
     * ルーチン実行処理(NonDefaultSession含む)
     * */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: this.executeType
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在する場合、正常判定を行う(存在しない場合は共通エラー)
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：停止処理(DefaultSession遷移(※worksupportId:12001の場合はNonDefaultSession遷移))へ遷移
              this.workSupportId !== 12001 ? await this.stop() : await this.stopWithNonDefaultSession();
              break;
            case config.STATUS_WSP_FAILED:
              // NonDefaultSession遷移で異常終了の場合、DefaultSession遷移を実行せずエラー画面に遷移
              switch (response.data.async_code) {
                case error.WSP_INITIALIZE_FAILED:
                  this.phase = PHASE.ERROR;
                  break;
                default:
                  await this.showCommonError();
                  break;
              }
              break;
            default:
              // 異常終了：共通エラーへ遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 停止処理(DefaultSession遷移で異常終了の場合、エラーへ遷移)(要求)
     */
    async stop() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在する場合、正常判定を行う(存在しない場合は共通エラー)
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：停止画面を表示
              this.phase = PHASE.STOP;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラーへ遷移
              await this.showCommonError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 停止処理(NonDefaultSession遷移で異常終了の場合、エラーへ遷移)の処理
     */
    async stopWithNonDefaultSession() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'session_transition'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在する場合、正常判定を行う(存在しない場合は共通エラー)
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：停止画面を表示
              this.phase = PHASE.STOP;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラーへ遷移
              await this.showCommonError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * エラー表示処理(DefaultSession遷移の結果に関わらずエラー表示)
     */
    async showCommonError() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // 通信結果に関わらずエラー画面を表示
        this.phase = PHASE.ERROR;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 通信エラー表示画面の終了処理
     */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // エラー遷移なし：機能終了
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * その他エラー発生時の表示
     */
    showUnexpectedError() {
      // エラー画面に遷移する
      this.phase = PHASE.CONNECT_FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },
};